import React, { useEffect, useState } from "react";
import { t } from "i18next";
import {
    useNavigate,
    useParams
} from "react-router-dom";
import {
    PlusCircleOutlined,
    PrinterOutlined,
    SendOutlined
} from "@ant-design/icons";
import swal from "sweetalert";
import moment from "moment/moment";
import { Util } from "common/util";
import {
    Button,
    InputDate,
    Col,
    Form,
    Modal,
    Row,
    ArrowLeftOutlined,
    Spin,
} from "common/elements";
import {
    getOne,
    submitTimesheet,
    approveTimesheet
} from "services/attendance";
import {
    insertNewDayoff,
    deleteDayoffById
} from "services/leave";

export default function TimesheetDetail() {
    const [loading, setLoading] = React.useState(true);
    const [submitting, setSubmitting] = React.useState(false);
    const navigate = useNavigate();
    const params = useParams();
    const [detailData, setDetailData] = useState({ totalHoliday: 0, totalHour: 0, totalProjectAllocated: 0 });
    const attendance = detailData.attendance;
    const leaveTypes = detailData.leaveTypes;
    const projects = detailData.projects;

    useEffect(() => {
        fetchData();

        document.getElementsByTagName("body")[0].setAttribute("class", "landscape");
        const style = document.createElement('style');
        style.innerHTML = `@media print {
            @page {
                size: landscape;
                margin: 5px
            }

            .ant-layout-header {
                display: none !important
            }

            #main-content-layout {
                margin: 0px !important
            }

            .main-table {
                height: auto;
                overflow: unset
            }

            .btn-navigation {
                display: none
            }
        }`;
        document.getElementById("timesheet").appendChild(style);

        return () => {
            document.getElementsByTagName("body")[0].setAttribute("class", "");
        };
        // eslint-disable-next-line
    }, []);

    const fetchData = () => {
        getOne(params.id)
            .then((response) => {
                if (response.data) {
                    setDetailData(response.data);
                }
            })
            .finally(() => setLoading(false));
    }

    const onSumbitTimesheet = () => {
        Util.getInstance()
            .sweetAlertConfirm(t("text_confrim"), "Once submitted, you will not be able to rollback this timesheet!")
            .then((willSubmit) => {
                if (willSubmit) {
                    setSubmitting(true);
                    submitTimesheet(params.id)
                        .then(() => {
                            onBack();
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }
            });
    }

    const onApproveTimesheet = () => {
        Util.getInstance()
            .sweetAlertConfirm(t("text_confrim"), "Once approved, you will not be able to rollback this timesheet!")
            .then((willApprove) => {
                if (willApprove) {
                    setSubmitting(true);
                    approveTimesheet(params.id)
                        .then(() => {
                            onBack();
                            window.location.reload()
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }
            });
    }

    const onDeleteDayoff = (dayoffId) => {
        swal({
            title: "Are you sure?",
            text: "Once deleted, you will not be able to rollback this dayoff!",
            icon: "warning",
            buttons: ["Cancel", "Yes"],
            dangerMode: true,
        })
            .then(willDelete => {
                if (willDelete) {
                    deleteDayoffById(dayoffId)
                        .then(() => {
                            swal({
                                title: "Deleted",
                                text: "Dayoff deleted successfully! ",
                                icon: "success",
                                timer: 1500
                            })
                                .then(() => {
                                    fetchData();
                                });
                        })
                }
            });
    }

    const onBack = () => {
        navigate("/timesheets");
    };

    function getMonthName(monthNumber) {
        const date = new Date();
        date.setMonth(monthNumber - 1);

        return date.toLocaleString("en-US", { month: "long" });
    }

    return (
        <Spin spinning={loading}>
            <div className="btn-navigation">
                <Row>
                    <Col span={12}>
                        <div className="pull-left">
                            <Button onClick={onBack}>
                                <ArrowLeftOutlined />
                                {t("text_back")}
                            </Button>
                            <span className="title">
                                Monthly Timesheet
                            </span>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className="pull-right">
                            <Button icon={<PrinterOutlined />} type="default" style={{ borderColor: "#d9d9d9", backgroundColor: "#fff", color: "rgba(0, 0, 0, 0.85)" }} onClick={() => window.print()}>
                                Print
                            </Button>

                            <NewDayOffForm reload={fetchData} />
                            {
                                // eslint-disable-next-line
                                attendance && attendance.status === "PENDING" && Util.getInstance().getCurrentUser()?.id === attendance.employeeId &&
                                <Button icon={<SendOutlined />} disabled={submitting} loading={submitting} onClick={onSumbitTimesheet} style={{ marginLeft: 15, backgroundColor: "rgb(184 184 0)", borderColor: "rgb(184 184 0)", color: "white" }}>
                                    {t("text_submit")}
                                </Button>
                            }

                            {
                                attendance && attendance.status === "SUBMITTED" && Util.getInstance().hasPermission("approve", "timesheet") && Util.getInstance().getCurrentUser()?.id !== attendance.employeeId &&
                                <Button disabled={submitting} loading={submitting} onClick={onApproveTimesheet}>
                                    {t("text_approve")}
                                </Button>
                            }
                        </div>
                    </Col>
                </Row>
            </div>

            <div className="main-table" id="timesheet">
                <table className="table-list-timesheet">
                    <thead className="all-td-th thead">
                        <tr className="null-header">
                            <th colSpan={detailData.projects && detailData.projects.length + detailData.leaveTypes && detailData.leaveTypes.length + 8}></th>
                            <th colSpan={3} className='none-bg-border'></th>
                            <th className="d-flex space-round">
                                <span>{getMonthName(attendance && attendance.month)}</span>
                                <span>{attendance && attendance.year}</span>
                            </th>
                        </tr>
                        <tr>
                            <th colSpan={5} className="header-table">
                                <span className="title">Last Name</span>
                                <span>{attendance && attendance.lastName}</span>
                            </th>
                            <th colSpan={detailData.leaveTypes && detailData.leaveTypes.length + 6} className="header-table">
                                <span className="title"> First Name</span>
                                <span>{attendance && attendance.firstName}</span>
                            </th>
                            <th className="d-flex space-round-th">
                                <span>Month</span>
                                <span>Year</span>
                            </th>
                        </tr>
                        <tr className="null-header">
                            <th colSpan={detailData.projects && detailData.projects.length + detailData.leaveTypes && detailData.leaveTypes.length + 8} style={{ padding: '13px' }}></th>
                            <th colSpan={4} className='none-bg-border'></th>
                        </tr>
                        <tr>
                            <th colSpan={detailData.projects && detailData.projects.length + detailData.leaveTypes && detailData.leaveTypes.length + 12} className="header-table">
                                <span className="title">Position</span>
                                <span>{attendance && attendance.position}</span>
                            </th>
                        </tr>
                        <tr style={{ lineHeight: '5px' }} className="border">
                            <th rowSpan={2} colSpan={3} className='none-bg-tb' style={{ width: '10%', border: 'none' }}>DONORS</th>
                            <th colSpan={detailData.projects && detailData.projects.length ? detailData.projects && detailData.projects.length : 3} className="work-houre">Hours Worked</th>
                            <th className="dis-border-table"></th>
                            <th colSpan={leaveTypes && leaveTypes.length + 1}>Regular Leaves Used</th>
                            <th className="dis-border-table"></th>
                            <th rowSpan={2}>Total Hours</th>
                            <th className="dis-border-table"></th>
                            <th rowSpan={2} className="remarks">Options</th>
                        </tr>
                        <tr style={{ lineHeight: '50px' }} className="border">
                            {
                                projects && projects.length > 0 ?
                                    projects.map((project, index) =>
                                        <th className="headaer-project" key={index} style={{ height: '90px', width: "10%" }}>
                                            <span className="title-header">{project.project}</span>
                                            <br />
                                            <span className="sub-title-header">{project.subProject}</span>
                                        </th>
                                    )
                                    : (<>
                                        <th style={{ height: '90px', width: "10%" }}></th>
                                        <th style={{ height: '90px', width: "10%" }}></th>
                                        <th style={{ height: '90px', width: "10%" }}></th>
                                    </>)

                            }
                            <th className="dis-border-table"></th>
                            <th style={{ width: "4%" }}>PH</th>
                            {
                                leaveTypes && leaveTypes.map((leaveType, index) =>
                                    <th style={{ width: "4%" }} key={index}>{leaveType.name && Util.getInstance().getFristCharater(leaveType.name)}</th>
                                )
                            }
                            <th className="dis-border-table"></th>
                        </tr>

                    </thead>
                    <tbody className="all-td-th-tbody">
                        <tr className="percent">
                            <td></td>
                            <td></td>
                            <td></td>
                            {
                                detailData.projects && detailData.projects.map((project, index) =>
                                    <td key={index}>{project.percentage * 100}%</td>
                                )
                            }

                            <th className="dis-border-table"></th>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td className="dis-border-table"></td>
                            <td></td>
                            <td className="dis-border-table"></td>
                            <td></td>
                        </tr>
                        {
                            detailData.detail && detailData.detail.map((data, index) =>
                                <tr key={index}>
                                    <td className={`${data.dayoff ? "grey-tb" : 'none-bg-tb'} date-columns`}>
                                        {moment(data.date).format('ddd')}
                                    </td>
                                    <td className={`${data.dayoff ? "grey-tb" : 'none-bg-tb'} date-columns`}>
                                        <div style={{ width: 60 }}>{moment(data.date).format("MMM D")}</div>
                                    </td>
                                    <td className={`${data.dayoff ? "grey-tb" : 'none-bg-tb'} date-columns`}>
                                        {data.dayoff ? '' : data.hour}
                                    </td>
                                    {
                                        detailData.projects.length ? detailData.projects.map((project, index) => <td key={index} className='body-text'>{data[`${project.subProject}`] ? data[`${project.subProject}`] : ""}</td>)
                                            :
                                            (<>
                                                <td></td>
                                                <td></td>
                                                <td></td>
                                            </>)
                                    }
                                    <td className="dis-border-table"></td>
                                    <td style={data.isHoliday ? { backgroundColor: "red" } : {}}>{data.holiday ? data.holiday : ""}</td>
                                    {
                                        leaveTypes && leaveTypes.map((leaveType, index) =>
                                            <td key={index}>
                                                {data.leave && data.leave.type === leaveType.type ? data.leave.numberOfHour : ""}
                                            </td>
                                        )
                                    }
                                    <td className="none-bg-tb dis-border-table"></td>
                                    <td className={`${data.dayoff ? "grey-tb" : ""} none-bg-tb`}>
                                        {data.total}
                                    </td>
                                    <th className="dis-border-table"></th>
                                    <td className={`${data.dayoff ? "grey-tb" : ""} none-bg-tb`}>
                                        {/* eslint-disable-next-line */}
                                        {data.dayoff && data.dayoffId && <a href="#" style={{ marginLeft: 15, color: "#ff4d4f" }} onClick={() => onDeleteDayoff(data.dayoffId)}>Delete</a>}
                                    </td>
                                </tr>
                            )
                        }

                        <tr className="none-bg-tb">
                            <td colSpan={3} className="total-footer">TOTAL</td>
                            {
                                projects && projects.length > 0 ? detailData.projects && detailData.projects.map((project, index) =>
                                    <td key={index} className="yellow-tb-td">{project.total ? project.total.toFixed(2) : 0}</td>
                                )
                                    :
                                    <>
                                        <td className="yellow-tb-td"></td>
                                        <td className="yellow-tb-td"></td>
                                        <td className="yellow-tb-td"></td>
                                    </>
                            }
                            <td className="dis-border-table"></td>
                            <td>{detailData?.totalHoliday}</td>
                            {
                                leaveTypes && leaveTypes.map((leaveType, leaveTypeIndex) => <td key={leaveTypeIndex}>{leaveType.total ? leaveType.total : 0}</td>)
                            }
                            <td className="dis-border-table"></td>
                            <td>{detailData?.totalHour}</td>
                            <th className="dis-border-table"></th>
                            <td></td>
                        </tr>
                        <tr className="none-bg-tb">
                            <td colSpan={3} className="total-footer">%</td>
                            {projects && projects.length > 0 ? detailData.projects.map((project, index) => <td key={index} className="yellow-tb-td">{project.percentage * 100}%</td>)
                                :
                                <>
                                    <td className="yellow-tb-td"></td>
                                    <td className="yellow-tb-td"></td>
                                    <td className="yellow-tb-td"></td>
                                </>
                            }
                            <td className="dis-border-table"></td>
                            <td></td>
                            {leaveTypes && leaveTypes.map((leaveType, leaveTypeIndex) => <td key={leaveTypeIndex}></td>)}
                            <td className="dis-border-table"></td>
                            <td>{detailData?.totalProjectAllocated * 100}</td>
                            <td className="dis-border-table"></td>
                            <td></td>
                        </tr>

                        <tr className="footer-table-list none-bg-tb">
                            <td colSpan={14}>I CERTIFY THAT THIS TIME SHEET IS CORRECT.</td>
                        </tr>
                        <tr className="footer-table-list none-bg-tb">
                            <td colSpan={detailData.projects && detailData.projects.length ? detailData.projects && detailData.projects.length + 3 : 6}>{attendance && attendance.status !== 'PENDING' ? attendance && attendance.employee : ''}</td>
                            <td className="dis-border-table"></td>
                            <td colSpan={9}>{attendance && attendance.submittedDate ? moment(attendance && attendance.submittedDate).format("YYYY-MM-DD") : ''}</td>
                        </tr>
                        <tr className="border-bt none-bg-tb">
                            <td colSpan={detailData.projects && detailData.projects.length ? detailData.projects && detailData.projects.length + 3 : 6}>Employee or Volunteer</td>
                            <td className="dis-border-table"></td>
                            <td colSpan={leaveTypes && leaveTypes.length + 1}>Date</td>
                        </tr>
                        <tr className="footer-table-list none-bg-tb" style={{ position: "relative" }}>
                            <td colSpan={14}>APPROVED BY</td>
                            {
                                attendance?.status === "APPROVED" &&
                                <td><span className="timesheet-stamp-approved">APPROVED</span></td>
                            }
                        </tr>
                        <tr className="footer-table-list none-bg-tb">
                            <td colSpan={detailData.projects && detailData.projects.length ? detailData.projects && detailData.projects.length + 3 : 6}>{attendance && attendance.status === 'APPROVED' ? attendance && attendance.approver : ''}</td>
                            <td className="dis-border-table"></td>
                            <td colSpan={9}>{attendance?.status === "APPROVED" && attendance.approvedDate ? moment(attendance && attendance.approvedDate).format("YYYY-MM-DD") : ''}</td>
                        </tr>
                        <tr className="border-bt none-bg-tb">
                            <td colSpan={detailData.projects && detailData.projects.length ? detailData.projects && detailData.projects.length + 3 : 6}>Direct Supervisor</td>
                            <td className="dis-border-table"></td>
                            <td colSpan={leaveTypes && leaveTypes.length + 1}>Date</td>
                        </tr>
                        <tr className="none-bg-tb">
                            <td colSpan={detailData.projects && detailData.projects.length ? detailData.projects && detailData.projects.length + 3 : 6} className='none-border-bg'></td>
                            <td className="dis-border-table"></td>
                            <td colSpan={leaveTypes && leaveTypes.length + 1} className='none-border-bg align-left'>Month: {attendance && attendance.month}</td>
                        </tr>
                    </tbody>
                </table>

            </div>
        </Spin>
    )
}

function NewDayOffForm({ reload }) {
    const [state, setState] = useState({ visible: false, submitting: false });

    const [form] = Form.useForm();

    const onClose = () => {
        setState(preState => ({ ...preState, visible: false }));
        form.resetFields();
    }

    const onOpen = () => {
        setState(preState => ({ ...preState, visible: true }));
    }

    const onChangeStart = (startDate) => {
        if (startDate) {
            form.setFieldsValue({ endDate: startDate })
        }
    }

    const onFinish = (values) => {
        const data = {
            startDate: moment(values.startDate).format("YYYY-MM-DD"),
            endDate: moment(values.endDate).format("YYYY-MM-DD")
        };

        setState(prevState => ({ ...prevState, submitting: true }));

        insertNewDayoff(data)
            .then(() => {
                swal({
                    title: "Created",
                    text: "Dayoff created successfully! ",
                    icon: "success",
                    timer: 1500
                })
                    .then((() => {
                        form.resetFields();
                        reload();
                    }));
            })
            .finally(() => {
                setState(prevState => ({ ...prevState, submitting: false }));
            })
            .catch(error => {
                swal({
                    title: "Duplicate Period Detected",
                    text: error?.response?.data?.error?.message,
                    icon: "error",
                    dangerMode: true
                });
            });
    }

    return <>
        <Button icon={<PlusCircleOutlined />} type="default" onClick={onOpen} style={{ borderColor: "#d9d9d9", backgroundColor: "#fff", color: "rgba(0, 0, 0, 0.85)", marginLeft: 10 }}>
            Add New Stand Down
        </Button>

        <Modal
            title="New Dayoff"
            visible={state.visible}
            onCancel={onClose}
        >
            <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
            >
                <InputDate
                    name="startDate"
                    label="Dayoff From"
                    required={true}
                    allowClear={true}
                    errorMessage="Please select start date"
                    format="ddd DD MMM, YYYY"
                    placeholder={"ddd DD MMM, YYYY"}
                    disabledDate={current => form.getFieldValue("endDate") && current > moment(form.getFieldValue("endDate"))}
                    onChange={onChangeStart}
                    style={{ width: "100%" }}
                />

                <InputDate
                    name="endDate"
                    label="Dayoff To"
                    required={true}
                    allowClear={true}
                    errorMessage="Please select end date"
                    format="ddd DD MMM, YYYY"
                    placeholder={"ddd DD MMM, YYYY"}
                    disabledDate={current => form.getFieldValue("startDate") && current < moment(form.getFieldValue("startDate"))}
                    style={{ width: "100%" }}
                />

                <div className="ant-modal-footer2">
                    <Button
                        className="btn-cancel"
                        onClick={onClose}
                        disabled={state.submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="btn-save"
                        type="primary"
                        htmlType="submit"
                        disabled={state.submitting}
                        loading={state.submitting}
                    >
                        Submit
                    </Button>
                </div>
            </Form>
        </Modal>
    </>
}

